import React, { useState, useMemo } from 'react';
import auth0 from 'auth0-js';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import { trackCheckInEnterEmail } from '../../../analytics';

import { Form, FloatingLabel, FormError } from '../../../components/form';
import { Button, Checkbox } from '../../../components/general';
import { GoogleBtn, LinkedInBtn } from '../../../components/thirdPartyBtns';

import config from '../../../config';

import { useAttendeeStore } from '../../../stores/attendee';

import { PublicEvent } from '../../../types';
import { isEmpty, getCheckInRedirectUri } from '../../../utils';

import { validateForm } from '../components/checkInValidations';
import { getTsAndCsText } from '../components/getTsAndCsText';

const { featureToggles, auth0Domain, auth0ClientID } = config;

const authenticate = (webAuth: any, eventId: string, email: string) => {
  return new Promise((resolve, reject) => {
    webAuth.authorize(
      {
        state: eventId,
        connection: 'email',
        nonce: 'nonce',
        login_hint: email,
      },
      (err: any, authResult: any) => (err ? reject(err) : resolve(authResult)),
    );
  });
};

interface Props {
  event: PublicEvent;
  isPreview?: boolean;
  width: number;
}

const EmailStage: React.FC<Props> = (props) => {
  const { event, isPreview, width } = props;

  const { setAttendeeDetails } = useAttendeeStore();

  const [data, setData] = useState({
    email: '',
    confirmEmail: '',
    isTsAndCs: true,
  });

  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { type, name, value, checked } = e.target;

    setData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'email') setAttendeeDetails({ attendeeEmail: value.trim() });

    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isPreview) return;

    const validationErrors = await validateForm({
      type: 'email',
      formData: data,
      isCheckConfirm: isShowConfirm,
    });

    // if errors found – set errors and stop here
    if (!isEmpty(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    // if it's not confirm stage – change to it and stop here
    if (!isShowConfirm) {
      setIsShowConfirm(true);
      return;
    }

    trackCheckInEnterEmail();

    // check in attendee
    const redirectUri = getCheckInRedirectUri(event.eventId);

    if (featureToggles.auth0) {
      setIsProcessing(true);
      const webAuth = new auth0.WebAuth({
        domain: auth0Domain,
        clientID: auth0ClientID,
        responseType: 'token id_token',
        scope: 'openid profile email',
        redirectUri,
      });
      return authenticate(webAuth, event.eventId, data.email);
    }

    // redirect to check-in stage page
    else navigate(redirectUri);
  };

  // on mobile – container is 90% of the page + 2 paddings by 1rem (16px)
  // on desktop – container is 500px + 2 paddings by 1rem (16px) – it fits 2 btns with 1rem (16px) gap
  const thirdPartyBtnWidth = useMemo(() => {
    return {
      class:
        (width < 640 ? 'w-full' : 'w-[calc((100%_-_1rem)_/_2)]') +
        ' max-w-[400px]',
      inPx: Math.round(
        width < 640
          ? (width / (isPreview ? 0.75 : 1)) * 0.9 - 16 * 2
          : (500 - 16 * 3) / 2,
      ),
    };
  }, [isPreview, width]);

  return (
    <Form onSubmit={handleSubmit} formClassName="w-full">
      <div className="text-center font-bold">
        Tell us where to send your personalised video
      </div>

      <FloatingLabel
        label="Enter your email"
        name="email"
        value={data.email}
        onChange={handleChange}
        isInvalid={!!errors.email}
        errMsg={errors.email}
        required
      />

      {isShowConfirm ? (
        <FloatingLabel
          label="Confirm email"
          name="confirmEmail"
          value={data.confirmEmail}
          onChange={handleChange}
          isInvalid={!!errors.confirmEmail}
          errMsg={errors.confirmEmail}
          required
        />
      ) : (
        <div>
          <Checkbox
            id="ci-t-and-c-checkbox"
            label={getTsAndCsText(event.tsAndCsLink, event.organiserName)}
            name="isTsAndCs"
            checked={data.isTsAndCs}
            onChange={handleChange}
            isInvalid={!!errors.isTsAndCs}
          />

          <FormError errMsg={errors.isTsAndCs} />
        </div>
      )}

      <Button
        className="w-full"
        disabled={!isEmpty(errors)}
        isSubmitBtn
        isProcessing={isProcessing}
      >
        Check-in
      </Button>

      <div className="flex items-center justify-center">
        <hr className="w-full my-4 border-grey-200 opacity-100" />

        <div className="absolute w-min px-2 text-black/[0.5] text-sm text-center bg-white">
          or
        </div>
      </div>

      <div
        className={classNames(
          'flex flex-wrap gap-4 items-center justify-center',
          isPreview && 'pointer-events-none',
        )}
      >
        {[GoogleBtn, LinkedInBtn].map((ThirdPartyBtn, index) => (
          <ThirdPartyBtn
            key={index}
            eventId={event.eventId}
            type="check-in"
            widthClass={thirdPartyBtnWidth.class}
            widthInPx={thirdPartyBtnWidth.inPx}
          />
        ))}
      </div>
    </Form>
  );
};

export default EmailStage;
